import React, { useState } from 'react';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import { motion, AnimatePresence } from 'framer-motion';

export const useCelebration = () => {
    const [showConfetti, setShowConfetti] = useState(false);
    const [recycleConfetti, setRecycleConfetti] = useState(false);
    const [flyer, setFlyer] = useState(null);
    const [celebrationMessage, setCelebrationMessage] = useState(null);

    const triggerCelebration = () => {
        // Reset states just in case
        setShowConfetti(false);
        setFlyer(null);
        setCelebrationMessage(null);

        // Small delay to allow reset to render if needed, or just set immediately
        setTimeout(() => {
            setShowConfetti(true);
            setRecycleConfetti(true);
        }, 10);

        // Launch Flyer
        const { innerHeight } = window;
        const flyerType = Math.random() > 0.6 ? 'unicorn' : Math.random() > 0.3 ? 'rocket' : 'phoenix';
        setFlyer({ type: flyerType, y: Math.random() * (innerHeight * 0.5) + 200 });

        // Show Text
        const messages = [
            "Stellar Work!", "Task Crushed!", "On Fire! 🔥", "Keep it up!", "Smooth!",
            "Well Done!", "Nice Move!", "Spectacular!", "Outstanding!", "Brilliant!",
            "Top Tier!", "Legendary!", "Epic Win!", "Goal Smashed!", "Victory!",
            "Champion!", "Unstoppable!", "Momentum!", "Pure Gold!", "Magic Touch!",
            "First Class!", "Elite!", "Masterful!", "Genius!", "Sharp!",
            "On Point!", "Bullseye! 🎯", "Direct Hit!", "Flawless!", "Sublime!",
            "Exemplary!", "Prime Time!", "Rocket Fuel 🚀", "Soaring!", "High Flying!",
            "Next Level!", "Game Changer!", "Power Move!", "Boss Mode! 😎", "In the Zone!",
            "Flow State!", "Rhythm!", "Groovy!", "Electric! ⚡", "Dynamite! 🧨",
            "Boom! 💥", "Solid!", "Nailed It!", "Got 'em!", "Too Easy!",
            "Clean!", "Crisp!", "Textbook!", "Superb!", "Magnificent!"
        ];
        setCelebrationMessage(messages[Math.floor(Math.random() * messages.length)]);

        // Stop Recycling
        setTimeout(() => setRecycleConfetti(false), 2500);

        // Cleanup
        setTimeout(() => {
            setShowConfetti(false);
            setFlyer(null);
            setCelebrationMessage(null);
        }, 5000);
    };

    return {
        triggerCelebration,
        celebrationState: { showConfetti, recycleConfetti, flyer, celebrationMessage }
    };
};

export const CelebrationOverlay = ({ state }) => {
    const { width, height } = useWindowSize();

    // Safety check if state is undefined
    if (!state) return null;

    const { showConfetti, recycleConfetti, flyer, celebrationMessage } = state;

    if (!showConfetti && !flyer && !celebrationMessage) return null;

    return (
        <>
            {showConfetti && (
                <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
                    <Confetti
                        width={width}
                        height={height}
                        numberOfPieces={400}
                        recycle={recycleConfetti}
                        gravity={0.15}
                        wind={0.01}
                    />
                </div>
            )}
            <AnimatePresence>
                {flyer && (
                    <motion.div
                        initial={{ x: -150, y: flyer.y, rotate: 10, scale: 0.5 }}
                        animate={{ x: width + 150, rotate: -10, scale: 1.2 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 3.5, ease: "easeInOut" }}
                        className="fixed z-[100] pointer-events-none text-6xl drop-shadow-2xl filter blur-[0.5px]"
                    >
                        {flyer.type === 'unicorn' && '🦄'}
                        {flyer.type === 'rocket' && '🚀'}
                        {flyer.type === 'phoenix' && '🦅'}
                    </motion.div>
                )}
            </AnimatePresence>
            <AnimatePresence>
                {celebrationMessage && (
                    <div className="fixed inset-0 flex items-center justify-center z-[100] pointer-events-none">
                        <motion.div
                            initial={{ scale: 0.5, opacity: 0, y: 50 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 1.5, opacity: 0, filter: 'blur(10px)' }}
                            transition={{ type: "spring", bounce: 0.5 }}
                            className="text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-500 drop-shadow-[0_0_30px_rgba(251,191,36,0.5)] tracking-tighter text-center px-4"
                        >
                            {celebrationMessage}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </>
    );
};
